/**
 * AgenticDeepDiveModal — showcase the full agentic intelligence behind ad generation.
 *
 * Four tabs designed for demos, sales calls, and IP discussions:
 *   Intelligence  — live narrative feed from the insight_log + phase activity cards
 *   Pipeline      — each phase with a plain-English description of what the AI does
 *   Cost Analysis — estimated per-phase and total cost breakdown
 *   Output        — quality metrics, ranked ads, and pipeline summary
 */
import { useState, useRef, useEffect } from 'react';
import {
  X,
  Building2,
  Palette,
  Package,
  Package2,
  Users,
  Network,
  Cpu,
  Lightbulb,
  Filter,
  FileEdit,
  Image as ImageIcon,
  Star,
  RefreshCw,
  Brain,
  CheckCircle2,
  Loader2,
  XCircle,
  Clock,
  DollarSign,
  BarChart2,
  Sparkles,
  TrendingUp,
  Zap,
  AlertTriangle,
  Ban,
  Info,
  GitFork,
  Layers2,
  ArrowDown,
  ArrowRight,
  Shuffle,
  Settings2,
  Lock,
  Unlock,
  ChevronRight,
  RotateCcw,
  ShieldCheck,
  Database,
  SlidersHorizontal,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { AdPackStatusResponse, StepLogEntry, InsightLogEntry } from '@/services/api';
import { phaseLabel } from './ExecutionLogPanel';

// ---------------------------------------------------------------------------
// Phase showcase metadata
// ---------------------------------------------------------------------------

type PhaseCategory = 'init' | 'data' | 'cognitive' | 'creative' | 'output';

interface PhaseMeta {
  icon: React.ElementType;
  tagline: string;
  detail: string;
  category: PhaseCategory;
}

const PHASE_META: Record<string, PhaseMeta> = {
  resolve_client: {
    icon: Building2,
    tagline: 'Workspace initialization',
    detail:
      'Establishes a secure, client-scoped execution context. Verifies credentials, loads organization config, and initializes the pipeline runtime.',
    category: 'init',
  },
  load_brief: {
    icon: FileEdit,
    tagline: 'Creative brief loading',
    detail:
      'Parses the campaign brief for objectives, tone, target scope, and strategic direction that guide all downstream creative decisions.',
    category: 'init',
  },
  load_brand: {
    icon: Palette,
    tagline: 'Brand intelligence',
    detail:
      'Retrieves brand guidelines, visual identity tokens, voice, creative constraints, and design system — grounding every asset in authentic brand expression.',
    category: 'data',
  },
  fetch_products: {
    icon: Package,
    tagline: 'Product catalog ingestion',
    detail:
      'Loads the full product catalog: descriptions, high-res images, pricing, attributes, and differentiators — ensuring creative is anchored in product truth.',
    category: 'data',
  },
  fetch_personas: {
    icon: Users,
    tagline: 'Buyer persona retrieval',
    detail:
      'Queries NeuroGraph for cognitive buyer persona profiles enriched with decision factors, intent signals, emotional drivers, and behavioral patterns.',
    category: 'data',
  },
  load_graph: {
    icon: Network,
    tagline: 'Cognitive graph connection',
    detail:
      'Connects to the NeuroGraph knowledge graph — loading buyer behavior patterns, market concepts, and cross-persona insights built from research data.',
    category: 'cognitive',
  },
  compute_affinities: {
    icon: Cpu,
    tagline: 'Affinity scoring engine',
    detail:
      'Runs semantic matching algorithms to compute affinity scores between each buyer persona and product. Uses graph embeddings to surface the highest-resonance pairings.',
    category: 'cognitive',
  },
  seed_scenes: {
    icon: Lightbulb,
    tagline: 'Scenario seeding',
    detail:
      'Generates contextually-rich buying scenarios — mapping when, where, and why each persona would encounter the product. Informed by cognitive graph signals.',
    category: 'cognitive',
  },
  select_scenarios: {
    icon: Filter,
    tagline: 'Scenario optimization',
    detail:
      'Ranks and selects the highest-scoring scenarios using affinity signals, cognitive resonance metrics, and creative diversity constraints.',
    category: 'cognitive',
  },
  interpret_brief: {
    icon: Brain,
    tagline: 'Brief interpretation',
    detail:
      'AI interprets the strategic brief direction and applies it to the creative generation context, resolving ambiguities and aligning with campaign objectives.',
    category: 'cognitive',
  },
  generate_briefs: {
    icon: FileEdit,
    tagline: 'AI creative brief synthesis',
    detail:
      'Synthesizes personalized creative briefs using multi-step LLM reasoning — combining persona insights, scenario context, product truth, and brand guidelines into a unified creative direction.',
    category: 'creative',
  },
  generate_images: {
    icon: ImageIcon,
    tagline: 'Visual asset generation',
    detail:
      'Generates on-brand visual assets using Gemini multi-modal image generation. Each image is guided by the creative brief with product and style reference inputs.',
    category: 'creative',
  },
  review_ads: {
    icon: Star,
    tagline: 'AI creative direction',
    detail:
      'Applies AI creative director scoring via the Cognitive Engagement Score (CES) framework — evaluating persona resonance, message clarity, emotional impact, and brand alignment.',
    category: 'creative',
  },
  refine_ads: {
    icon: RefreshCw,
    tagline: 'Iterative refinement loop',
    detail:
      'Iteratively refines low-scoring ads using cognitive feedback. Rewrites hooks, adjusts visual direction, and re-scores until ads meet quality thresholds.',
    category: 'output',
  },
  generate_lp_briefs: {
    icon: FileEdit,
    tagline: 'Landing page brief generation',
    detail:
      "Generates matched landing page creative briefs for campaign consistency — ensuring the post-click experience aligns with each ad's creative angle.",
    category: 'output',
  },
  package: {
    icon: Package2,
    tagline: 'Pack assembly & cloud delivery',
    detail:
      'Assembles the final ad pack with ranked ads, quality metadata, and strategy distribution. Uploads to cloud storage and generates a shareable manifest.',
    category: 'output',
  },
};

function getPhaseMeta(phase: string): PhaseMeta {
  return (
    PHASE_META[phase] ?? {
      icon: Zap,
      tagline: 'Pipeline step',
      detail: 'Executes a pipeline step as part of the ad generation workflow.',
      category: 'init',
    }
  );
}

const CATEGORY_STYLE: Record<PhaseCategory, { label: string; badge: string; bar: string; dot: string }> = {
  init: {
    label: 'Init',
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
    bar: 'bg-slate-400',
    dot: 'bg-slate-400',
  },
  data: {
    label: 'Data',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    bar: 'bg-blue-500',
    dot: 'bg-blue-500',
  },
  cognitive: {
    label: 'Cognitive',
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    bar: 'bg-purple-500',
    dot: 'bg-purple-500',
  },
  creative: {
    label: 'Creative',
    badge: 'bg-teal-50 text-teal-700 border-teal-200',
    bar: 'bg-brand-teal',
    dot: 'bg-brand-teal',
  },
  output: {
    label: 'Output',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
  },
};

// ---------------------------------------------------------------------------
// Cost estimation (model-based, until backend tracks actual costs)
// ---------------------------------------------------------------------------

interface PhaseCostConfig {
  model: string;
  baseCost: number;
  costPerItem: number;
}

const PHASE_COST: Record<string, PhaseCostConfig> = {
  resolve_client: { model: 'API', baseCost: 0.0001, costPerItem: 0 },
  load_brief: { model: 'API', baseCost: 0.0001, costPerItem: 0 },
  load_brand: { model: 'API', baseCost: 0.0002, costPerItem: 0 },
  fetch_products: { model: 'API', baseCost: 0.001, costPerItem: 0 },
  fetch_personas: { model: 'API', baseCost: 0.001, costPerItem: 0 },
  load_graph: { model: 'NeuroGraph', baseCost: 0.003, costPerItem: 0 },
  compute_affinities: { model: 'Embedding API', baseCost: 0.005, costPerItem: 0.001 },
  seed_scenes: { model: 'Gemini 2.0 Flash', baseCost: 0.01, costPerItem: 0.003 },
  select_scenarios: { model: 'Gemini 2.0 Flash', baseCost: 0.003, costPerItem: 0.001 },
  interpret_brief: { model: 'Gemini 2.0 Flash', baseCost: 0.005, costPerItem: 0 },
  generate_briefs: { model: 'Gemini 2.0 Pro', baseCost: 0, costPerItem: 0.008 },
  generate_images: { model: 'Gemini Imagen 3', baseCost: 0, costPerItem: 0.04 },
  review_ads: { model: 'Gemini 2.0 Flash', baseCost: 0, costPerItem: 0.003 },
  refine_ads: { model: 'Gemini 2.0 Pro', baseCost: 0, costPerItem: 0.006 },
  generate_lp_briefs: { model: 'Gemini 2.0 Pro', baseCost: 0, costPerItem: 0.005 },
  package: { model: 'API', baseCost: 0.001, costPerItem: 0 },
};

export function estimatePhaseCost(step: StepLogEntry): number {
  const cfg = PHASE_COST[step.step_name];
  if (!cfg) return 0;
  const items = step.items_completed || Math.max(step.items_total, 1);
  return cfg.baseCost + cfg.costPerItem * items;
}

export function estimateTotalCost(steps: StepLogEntry[]): number {
  return steps.reduce((sum, s) => sum + estimatePhaseCost(s), 0);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(seconds?: number): string {
  if (!seconds || seconds < 1) return '< 1s';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatCost(usd: number): string {
  if (usd < 0.001) return `$${(usd * 100).toFixed(4)}¢`;
  if (usd < 1) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

function elapsed(seconds?: number): string {
  if (!seconds || seconds < 1) return '< 1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function stepStatusIcon(status: string, size = 14) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 size={size} className="text-emerald-500 flex-shrink-0" />;
    case 'failed':
      return <XCircle size={size} className="text-rose-500 flex-shrink-0" />;
    case 'running':
      return <Loader2 size={size} className="text-brand-teal animate-spin flex-shrink-0" />;
    case 'skipped':
      return <Ban size={size} className="text-slate-400 flex-shrink-0" />;
    default:
      return <Clock size={size} className="text-slate-300 flex-shrink-0" />;
  }
}

// ---------------------------------------------------------------------------
// Tab types
// ---------------------------------------------------------------------------

type TabId = 'intelligence' | 'architecture' | 'pipeline' | 'cost' | 'output';

// ---------------------------------------------------------------------------
// Intelligence tab
// ---------------------------------------------------------------------------

function InsightEntry({
  entry,
  isLatest,
  isLive,
  highlighted,
}: {
  entry: InsightLogEntry;
  isLatest: boolean;
  isLive: boolean;
  highlighted: boolean;
}) {
  const meta = getPhaseMeta(entry.phase);
  const cat = CATEGORY_STYLE[meta.category];
  return (
    <div
      className={clsx(
        'flex items-start gap-3 px-4 py-3 rounded-xl transition-all border',
        highlighted
          ? 'bg-brand-teal/5 border-brand-teal/20'
          : isLatest && isLive
            ? 'bg-brand-teal/5 border-brand-teal/15'
            : 'bg-white border-transparent hover:border-slate-200',
      )}
    >
      <span className="text-[10px] font-mono text-slate-400 shrink-0 w-20 pt-1">
        {formatTime(entry.timestamp)}
      </span>
      <span
        className={clsx(
          'inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded-full border whitespace-nowrap shrink-0 mt-0.5',
          cat.badge,
        )}
      >
        {phaseLabel(entry.phase)}
      </span>
      <p className="text-sm text-slate-700 flex-1 leading-relaxed">{entry.insight}</p>
      {entry.progress > 0 && (
        <span className="text-[10px] font-mono text-slate-400 shrink-0 pt-1">
          {Math.round(entry.progress)}%
        </span>
      )}
    </div>
  );
}

function IntelligenceTab({
  job,
  highlightedPhase,
  onPhaseClick,
}: {
  job: AdPackStatusResponse;
  highlightedPhase: string | null;
  onPhaseClick: (phase: string) => void;
}) {
  const insights = job.execution_log?.insight_log ?? [];
  const steps = job.execution_log?.steps ?? [];
  const isLive = ['running', 'pending', 'queued'].includes(job.status);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLive && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [insights.length, isLive]);

  const phaseInsightCounts: Record<string, number> = {};
  for (const entry of insights) {
    phaseInsightCounts[entry.phase] = (phaseInsightCounts[entry.phase] ?? 0) + 1;
  }

  // Build all phases in execution order
  const allPhases = steps.map((s) => s.step_name);

  return (
    <div className="flex gap-6 h-full min-h-0">
      {/* Left: Phase activity cards */}
      <div className="w-56 shrink-0 overflow-y-auto space-y-2 pr-1">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3 px-1">
          Phase Activity
        </p>
        {allPhases.length === 0 && (
          <p className="text-xs text-slate-400 px-1">No phase data yet.</p>
        )}
        {allPhases.map((phase) => {
          const step = steps.find((s) => s.step_name === phase);
          const meta = getPhaseMeta(phase);
          const cat = CATEGORY_STYLE[meta.category];
          const Icon = meta.icon;
          const count = phaseInsightCounts[phase] ?? 0;
          const isHighlighted = highlightedPhase === phase;
          return (
            <button
              key={phase}
              onClick={() => onPhaseClick(phase)}
              className={clsx(
                'w-full text-left p-3 rounded-xl border transition-all',
                isHighlighted
                  ? 'bg-navy-900 border-navy-900 text-white'
                  : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm',
              )}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div
                  className={clsx(
                    'w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0',
                    isHighlighted ? 'bg-white/10' : cat.badge,
                  )}
                >
                  <Icon size={11} />
                </div>
                <span
                  className={clsx(
                    'text-[11px] font-semibold leading-tight',
                    isHighlighted ? 'text-white' : 'text-navy-900',
                  )}
                >
                  {phaseLabel(phase)}
                </span>
              </div>
              <div
                className={clsx(
                  'flex items-center justify-between text-[10px]',
                  isHighlighted ? 'text-white/60' : 'text-slate-400',
                )}
              >
                <span>{count} events</span>
                {step?.duration_seconds != null && (
                  <span>{formatDuration(step.duration_seconds)}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Right: Narrative feed */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Intelligence Narrative
            </p>
            {insights.length > 0 && (
              <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 font-mono">
                {insights.length} events
              </span>
            )}
          </div>
          {isLive && (
            <span className="flex items-center gap-1.5 text-[10px] font-medium text-brand-teal">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-teal" />
              </span>
              LIVE
            </span>
          )}
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto space-y-1 pr-1"
          style={{ scrollBehavior: 'smooth' }}
        >
          {insights.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <Brain size={32} className="text-slate-200 mb-3" />
              <p className="text-sm text-slate-500 font-medium">No narrative events recorded</p>
              <p className="text-xs text-slate-400 mt-1 max-w-xs">
                Intelligence narrative is captured via Firestore streaming.
                Run a job with live Firestore access to see the AI reasoning in real-time.
              </p>
            </div>
          ) : (
            <>
              {(highlightedPhase
                ? insights.filter((e) => e.phase === highlightedPhase)
                : insights
              ).map((entry, i) => (
                <InsightEntry
                  key={i}
                  entry={entry}
                  isLatest={i === insights.length - 1}
                  isLive={isLive}
                  highlighted={highlightedPhase === entry.phase}
                />
              ))}
              {highlightedPhase && insights.filter((e) => e.phase === highlightedPhase).length === 0 && (
                <div className="flex flex-col items-center justify-center h-32 text-center">
                  <p className="text-xs text-slate-400">
                    No insights recorded for {phaseLabel(highlightedPhase)}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Architecture tab — visual pipeline blueprint + unique elements
// ---------------------------------------------------------------------------

interface LayerDef {
  id: string;
  label: string;
  category: PhaseCategory;
  badge?: string;
  phases: string[];
  description: string;
  unique?: boolean;
}

const PIPELINE_LAYERS: LayerDef[] = [
  {
    id: 'inputs',
    label: 'Data Inputs',
    category: 'data',
    phases: ['resolve_client', 'load_brief', 'load_brand', 'fetch_products', 'fetch_personas'],
    description: 'Client config, brand identity, product catalog, and buyer persona profiles are loaded and validated.',
  },
  {
    id: 'cognitive',
    label: 'Cognitive Intelligence Layer',
    category: 'cognitive',
    badge: 'NeuroGraph',
    phases: ['load_graph', 'compute_affinities', 'seed_scenes', 'select_scenarios', 'interpret_brief'],
    description:
      'Connects to the knowledge graph, scores persona-product affinity, composes contextual scenes, and selects the optimal buying scenarios per persona.',
    unique: true,
  },
  {
    id: 'synthesis',
    label: 'Creative Synthesis',
    category: 'creative',
    phases: ['generate_briefs'],
    description:
      'Fuses persona signals, scenario context, brand guidelines, product truth, and ontology templates into structured creative briefs via multi-step LLM reasoning.',
    unique: true,
  },
  {
    id: 'generation',
    label: 'Generation Engine',
    category: 'creative',
    phases: ['generate_images'],
    description:
      'Multi-modal image generation guided by the creative brief, using product reference images and brand style references for on-brand visual output.',
  },
];

const SELF_EVAL_LAYER = {
  label: 'Self-Evaluation Loop',
  category: 'output' as PhaseCategory,
  badge: 'Proprietary',
  description:
    'AI creative director scores each ad using CES (≥ 90%) and L0 factor coverage (≥ 80%). Failing ads enter the refinement engine for iterative improvement — up to configurable max iterations.',
};

const OUTPUT_LAYER: LayerDef = {
  id: 'output',
  label: 'Output & Delivery',
  category: 'output',
  phases: ['generate_lp_briefs', 'package'],
  description: 'Ads are ranked by CES, a manifest is generated, and the pack is delivered to cloud storage.',
};

interface UniqueElement {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  detail: string;
  badge?: string;
  dataPoint?: (job: AdPackStatusResponse) => string | null;
}

const UNIQUE_ELEMENTS: UniqueElement[] = [
  {
    icon: Database,
    title: 'Affinity Engine',
    subtitle: 'BigQuery × LLM hybrid scoring',
    detail:
      'Pre-computed affinity scores from BigQuery production buyer data. Falls back to real-time LLM scoring if pre-computed scores are unavailable, ensuring every creative brief is grounded in authentic resonance data.',
    badge: 'NeuroGraph Only',
    dataPoint: (job) => {
      const insight = job.execution_log?.insight_log?.find((e) => e.phase === 'compute_affinities');
      if (!insight) return null;
      if (insight.insight.includes('BigQuery')) return 'Source: BigQuery production';
      if (insight.insight.includes('LLM')) return 'Source: LLM scoring (fallback)';
      return null;
    },
  },
  {
    icon: Layers2,
    title: 'Scene Composition Graph',
    subtitle: 'Multi-modal contextual scene seeds',
    detail:
      'For each product, Gemini multi-modal generates a reference scene image grounded in brand context directives (e.g., "outdoor active use", "kitchen lifestyle"). These scenes anchor the creative brief in visual truth rather than text-only prompts.',
    badge: 'NeuroGraph Only',
    dataPoint: (job) => {
      const insight = job.execution_log?.insight_log?.find(
        (e) => e.phase === 'seed_scenes' && e.insight.includes('Scene seeding complete'),
      );
      if (insight) return insight.insight;
      return null;
    },
  },
  {
    icon: GitFork,
    title: 'Graph-Driven Scenario Selection',
    subtitle: 'NeuroGraph buying scenarios → per-persona routing',
    detail:
      'The NeuroGraph knowledge graph provides a pool of research-backed buyer scenarios. An LLM agent selects and routes the highest-affinity scenario to each persona-product pair — ensuring each ad reflects a real buying moment, not a generic hook.',
    badge: 'NeuroGraph Only',
    dataPoint: (job) => {
      const step = job.execution_log?.steps?.find((s) => s.step_name === 'select_scenarios');
      if (step && step.items_completed > 0) return `${step.items_completed} scenario assignments`;
      return null;
    },
  },
  {
    icon: ShieldCheck,
    title: 'Dual-Quality Gate',
    subtitle: 'CES ≥ 90% · L0 factor coverage ≥ 80%',
    detail:
      'Every ad must pass two quality thresholds: a Cognitive Engagement Score ≥ 90% (overall creative resonance) AND ≥ 80% coverage of the persona\'s Level-0 decision factors (the non-negotiable purchase drivers). Ads failing either gate are automatically queued for refinement.',
    badge: 'Proprietary Framework',
  },
  {
    icon: RotateCcw,
    title: 'Iterative Refinement Loop',
    subtitle: 'Self-improving creative with convergence scoring',
    detail:
      'Failing ads re-enter the pipeline with full context — original brief, review feedback, persona factors, and ontology guidance. Each refinement iteration rewrites the brief, regenerates the image, and re-scores. The loop exits when thresholds are met or max iterations are reached.',
    dataPoint: (job) => {
      const step = job.execution_log?.steps?.find((s) => s.step_name === 'refine_ads');
      if (step?.status === 'completed') return `${step.items_completed} ads refined`;
      if (step?.status === 'running') return 'Refinement in progress...';
      const skipped = job.execution_log?.insight_log?.find(
        (e) => e.phase === 'refine_ads' && e.insight.includes('passed quality threshold'),
      );
      if (skipped) return 'All ads passed — refinement skipped';
      return null;
    },
  },
  {
    icon: SlidersHorizontal,
    title: 'Ontology-Guided Creative',
    subtitle: 'Category templates + style reference ads',
    detail:
      'A brand-specific creative ontology provides template guidance and style reference images from real high-performing ads. The brief synthesis uses these to select the optimal creative template and maintain visual brand consistency across the pack.',
    badge: 'Configurable',
  },
];

const CONFIGURABLE_PARAMS = [
  {
    id: 'scene_seeding',
    label: 'Scene Seeding',
    phase: 'seed_scenes',
    description: 'Generates multi-modal reference scenes per product before brief synthesis.',
    currentLabel: (job: AdPackStatusResponse) =>
      job.phases_completed.includes('seed_scenes') || job.phases_remaining.includes('seed_scenes')
        ? 'Enabled'
        : 'Skipped',
    future: 'Override with custom scene images or disable for speed runs.',
  },
  {
    id: 'refinement',
    label: 'Refinement Loop',
    phase: 'refine_ads',
    description: 'Iterative ad improvement until quality thresholds are met.',
    currentLabel: (job: AdPackStatusResponse) =>
      job.phases_completed.includes('refine_ads') || job.phases_remaining.includes('refine_ads')
        ? 'Enabled'
        : 'Skipped',
    future: 'Set max iterations, custom CES threshold, or disable for draft runs.',
  },
  {
    id: 'creative_strategy',
    label: 'Creative Strategy',
    phase: 'generate_briefs',
    description: 'Controls how the LLM selects visual approach (auto = model selects).',
    currentLabel: (_job: AdPackStatusResponse) => 'Auto (model selects)',
    future: 'Lock to: product_showcase, lifestyle_action, problem_solution, social_proof.',
  },
  {
    id: 'affinity_source',
    label: 'Affinity Source',
    phase: 'compute_affinities',
    description: 'Where persona-product affinity scores come from.',
    currentLabel: (job: AdPackStatusResponse) => {
      const insight = job.execution_log?.insight_log?.find((e) => e.phase === 'compute_affinities');
      if (insight?.insight.includes('BigQuery')) return 'BigQuery production';
      if (insight?.insight.includes('LLM')) return 'LLM generated';
      return 'Auto';
    },
    future: 'Pin to BigQuery, override with manual scores, or use A/B comparison.',
  },
  {
    id: 'persona_routing',
    label: 'Persona × Product Routing',
    phase: 'compute_affinities',
    description: 'How persona-product ad pairs are constructed from affinity scores.',
    currentLabel: (_job: AdPackStatusResponse) => 'Auto (affinity ranked)',
    future: 'Specify explicit persona:product pairs, or set minimum affinity threshold.',
  },
  {
    id: 'image_model',
    label: 'Generation Model',
    phase: 'generate_images',
    description: 'AI model used for image generation.',
    currentLabel: (_job: AdPackStatusResponse) => 'Gemini Imagen 3',
    future: 'Swap in custom fine-tuned models, or chain multiple generation passes.',
  },
];

function ArchitecturePipelineFlow({ job }: { job: AdPackStatusResponse }) {
  const completedPhases = new Set(job.phases_completed);
  const currentPhase = job.pipeline_phase;

  function phaseChipStyle(phase: string) {
    if (completedPhases.has(phase)) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (currentPhase === phase) return 'bg-brand-teal/15 text-brand-teal border-brand-teal/30 animate-pulse';
    return 'bg-slate-100 text-slate-400 border-slate-200';
  }

  return (
    <div className="relative">
      {PIPELINE_LAYERS.map((layer, i) => {
        const catStyle = CATEGORY_STYLE[layer.category];
        const visiblePhases = layer.phases.filter(
          (p) =>
            completedPhases.has(p) ||
            p === currentPhase ||
            job.phases_remaining.includes(p) ||
            (job.execution_log?.steps ?? []).some((s) => s.step_name === p),
        );
        const displayPhases = visiblePhases.length > 0 ? visiblePhases : layer.phases.slice(0, 3);

        return (
          <div key={layer.id}>
            <div
              className={clsx(
                'rounded-xl border-l-4 bg-white border border-slate-200 p-4',
                layer.unique && 'border-slate-200',
              )}
              style={{ borderLeftColor: layer.unique ? '#7c3aed' : undefined }}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-navy-900">{layer.label}</span>
                  {layer.badge && (
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-purple-100 text-purple-700 border border-purple-200 rounded-full">
                      {layer.badge}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">{layer.description}</p>
              <div className="flex flex-wrap gap-2">
                {displayPhases.map((phase) => (
                  <span
                    key={phase}
                    className={clsx(
                      'text-[10px] font-bold px-2.5 py-1 rounded-full border transition-all',
                      phaseChipStyle(phase),
                    )}
                  >
                    {phaseLabel(phase)}
                  </span>
                ))}
              </div>
            </div>

            {/* Connector */}
            {i < PIPELINE_LAYERS.length - 1 && (
              <div className="flex justify-center py-1.5">
                <ArrowDown size={14} className="text-slate-300" />
              </div>
            )}
          </div>
        );
      })}

      {/* Arrow into self-eval */}
      <div className="flex justify-center py-1.5">
        <ArrowDown size={14} className="text-slate-300" />
      </div>

      {/* Self-evaluation loop */}
      <div className="rounded-xl border-l-4 border border-amber-200 bg-amber-50 p-4" style={{ borderLeftColor: '#d97706' }}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-navy-900">{SELF_EVAL_LAYER.label}</span>
            <span className="text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 rounded-full">
              {SELF_EVAL_LAYER.badge}
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-600 mb-3 leading-relaxed">{SELF_EVAL_LAYER.description}</p>
        <div className="grid grid-cols-2 gap-3">
          {/* Review */}
          <div className="bg-white rounded-lg border border-amber-200 p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <Star size={11} className="text-amber-600" />
              <span className="text-xs font-bold text-slate-700">AI Creative Review</span>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500">CES threshold</span>
                <span className="font-mono font-bold text-amber-700">≥ 90%</span>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500">L0 factor coverage</span>
                <span className="font-mono font-bold text-amber-700">≥ 80%</span>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500">Semantic scoring</span>
                <span className="font-mono font-bold text-emerald-600">enabled</span>
              </div>
            </div>
          </div>
          {/* Refine */}
          <div className="bg-white rounded-lg border border-amber-200 p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <RotateCcw size={11} className="text-amber-600" />
              <span className="text-xs font-bold text-slate-700">Refinement Engine</span>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500">Max iterations</span>
                <span className="font-mono font-bold text-slate-700">configurable</span>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500">Re-generates</span>
                <span className="font-mono font-bold text-slate-700">brief + image</span>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500">Context</span>
                <span className="font-mono font-bold text-slate-700">full + feedback</span>
              </div>
            </div>
          </div>
        </div>
        {/* Loop indicator */}
        <div className="mt-3 flex items-center gap-2 text-[10px] text-amber-700">
          <RotateCcw size={10} />
          <span>Fail → refine → re-score → repeat until pass or max iterations reached</span>
        </div>
      </div>

      {/* Arrow to output */}
      <div className="flex justify-center py-1.5">
        <ArrowDown size={14} className="text-slate-300" />
      </div>

      {/* Output layer */}
      {(() => {
        const catStyle = CATEGORY_STYLE[OUTPUT_LAYER.category];
        const displayPhases = OUTPUT_LAYER.phases;
        return (
          <div className="rounded-xl border-l-4 bg-white border border-slate-200 p-4" style={{ borderLeftColor: '#10b981' }}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <span className="text-sm font-bold text-navy-900">{OUTPUT_LAYER.label}</span>
            </div>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed">{OUTPUT_LAYER.description}</p>
            <div className="flex flex-wrap gap-2">
              {displayPhases.map((phase) => (
                <span
                  key={phase}
                  className={clsx(
                    'text-[10px] font-bold px-2.5 py-1 rounded-full border',
                    completedPhases.has(phase)
                      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                      : 'bg-slate-100 text-slate-400 border-slate-200',
                  )}
                >
                  {phaseLabel(phase)}
                </span>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function ArchitectureTab({ job }: { job: AdPackStatusResponse }) {
  const [openConfig, setOpenConfig] = useState<string | null>(null);

  return (
    <div className="overflow-y-auto pr-1 h-full">
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-6 pb-4">
        {/* Left: Pipeline Blueprint */}
        <div className="space-y-4">
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
              Pipeline Blueprint
            </p>
            <p className="text-xs text-slate-500">
              Visual architecture of the agentic ad generation system. Purple layers are powered by NeuroGraph-only capabilities.
            </p>
          </div>
          <ArchitecturePipelineFlow job={job} />
        </div>

        {/* Right: Unique elements + config */}
        <div className="space-y-6">
          {/* Unique differentiators */}
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">
              Unique Intelligence Elements
            </p>
            <div className="space-y-3">
              {UNIQUE_ELEMENTS.map((el) => {
                const Icon = el.icon;
                const dataPoint = el.dataPoint?.(job);
                return (
                  <div key={el.title} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600 shrink-0 border border-purple-100">
                        <Icon size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-bold text-navy-900">{el.title}</span>
                          {el.badge && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 bg-purple-100 text-purple-700 border border-purple-200 rounded-full whitespace-nowrap">
                              {el.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium mb-1.5">{el.subtitle}</p>
                        <p className="text-[11px] text-slate-600 leading-relaxed">{el.detail}</p>
                        {dataPoint && (
                          <div className="mt-2 flex items-center gap-1.5 text-[10px] font-medium text-brand-teal">
                            <CheckCircle2 size={10} />
                            <span>{dataPoint}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pipeline configuration */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Pipeline Configuration
              </p>
              <span className="text-[9px] font-bold px-1.5 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 rounded-full">
                Roadmap: User Customizable
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              These parameters are currently set per-job. Future versions will expose a pipeline editor for customization, chaining, and self-adaptive configuration.
            </p>
            <div className="space-y-2">
              {CONFIGURABLE_PARAMS.map((param) => {
                const currentValue = param.currentLabel(job);
                const isOpen = openConfig === param.id;
                return (
                  <div key={param.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                      onClick={() => setOpenConfig(isOpen ? null : param.id)}
                    >
                      <div className="flex items-center gap-3 text-left">
                        <Settings2 size={12} className="text-slate-400 shrink-0" />
                        <div>
                          <span className="text-xs font-semibold text-navy-900">{param.label}</span>
                          <span className="ml-2 text-[10px] font-mono text-slate-500">{currentValue}</span>
                        </div>
                      </div>
                      <ChevronRight
                        size={12}
                        className={clsx('text-slate-400 transition-transform', isOpen && 'rotate-90')}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-3 border-t border-slate-100">
                        <p className="text-[11px] text-slate-600 mt-2 mb-2 leading-relaxed">{param.description}</p>
                        <div className="flex items-start gap-2 bg-purple-50 rounded-lg px-3 py-2 border border-purple-100">
                          <Unlock size={10} className="text-purple-500 mt-0.5 shrink-0" />
                          <p className="text-[10px] text-purple-700 leading-relaxed">
                            <span className="font-bold">Roadmap:</span> {param.future}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline tab
// ---------------------------------------------------------------------------

function PipelineTab({ job }: { job: AdPackStatusResponse }) {
  const steps = job.execution_log?.steps ?? [];
  const allPhases = [
    ...steps.map((s) => s.step_name),
    ...job.phases_completed.filter((p) => !steps.some((s) => s.step_name === p)),
    ...job.phases_remaining,
  ];
  const uniquePhases = [...new Set(allPhases)];
  const stepMap = Object.fromEntries(steps.map((s) => [s.step_name, s]));

  const hasPhases = uniquePhases.length > 0;

  return (
    <div className="space-y-3 overflow-y-auto pr-1">
      {!hasPhases && (
        <div className="flex flex-col items-center justify-center h-48 text-center">
          <Zap size={32} className="text-slate-200 mb-3" />
          <p className="text-sm text-slate-500">No pipeline data available yet.</p>
        </div>
      )}
      {uniquePhases.map((phase, idx) => {
        const step = stepMap[phase];
        const meta = getPhaseMeta(phase);
        const cat = CATEGORY_STYLE[meta.category];
        const Icon = meta.icon;
        const isCompleted = job.phases_completed.includes(phase);
        const isRunning = phase === job.pipeline_phase && !isCompleted;
        const status = isCompleted ? 'completed' : isRunning ? 'running' : 'pending';
        const isLast = idx === uniquePhases.length - 1;

        return (
          <div key={phase} className="flex gap-4">
            {/* Timeline connector */}
            <div className="flex flex-col items-center w-8 shrink-0">
              <div
                className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10',
                  isCompleted && 'bg-emerald-500 text-white',
                  isRunning && 'bg-brand-teal text-white',
                  !isCompleted && !isRunning && 'bg-slate-100 text-slate-400 border-2 border-slate-200',
                )}
              >
                {isRunning ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Icon size={13} />
                )}
              </div>
              {!isLast && (
                <div className={clsx('w-0.5 flex-1 mt-1', isCompleted ? 'bg-emerald-200' : 'bg-slate-100')} />
              )}
            </div>

            {/* Card */}
            <div
              className={clsx(
                'flex-1 mb-4 rounded-xl border p-4 transition-all',
                isRunning
                  ? 'bg-brand-teal/5 border-brand-teal/25'
                  : isCompleted
                    ? 'bg-white border-slate-200'
                    : 'bg-slate-50 border-slate-100',
              )}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm text-navy-900">{phaseLabel(phase)}</span>
                  <span
                    className={clsx(
                      'text-[10px] font-bold px-2 py-0.5 rounded-full border',
                      cat.badge,
                    )}
                  >
                    {cat.label}
                  </span>
                  {isRunning && (
                    <span className="text-[10px] font-bold text-brand-teal">RUNNING</span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0 text-[11px] text-slate-400">
                  {step?.duration_seconds != null && (
                    <span className="font-mono">{formatDuration(step.duration_seconds)}</span>
                  )}
                  {step && step.items_total > 0 && (
                    <span>
                      {step.items_completed}/{step.items_total} items
                    </span>
                  )}
                </div>
              </div>

              <p className="text-xs text-slate-500 font-medium mb-1">{meta.tagline}</p>
              <p className="text-xs text-slate-600 leading-relaxed">{meta.detail}</p>

              {step?.error && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-rose-600 bg-rose-50 rounded-lg px-3 py-2 border border-rose-200">
                  <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                  <span className="font-mono break-all">{step.error}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cost Analysis tab
// ---------------------------------------------------------------------------

function CostAnalysisTab({ job }: { job: AdPackStatusResponse }) {
  const steps = job.execution_log?.steps ?? [];
  const completedSteps = steps.filter((s) => s.status === 'completed' || s.status === 'running');

  interface CostEntry {
    phase: string;
    model: string;
    items: number;
    cost: number;
    category: PhaseCategory;
  }

  const entries: CostEntry[] = completedSteps.map((step) => {
    const cfg = PHASE_COST[step.step_name] ?? { model: 'API', baseCost: 0, costPerItem: 0 };
    const meta = getPhaseMeta(step.step_name);
    const items = step.items_completed || Math.max(step.items_total, 1);
    return {
      phase: step.step_name,
      model: cfg.model,
      items,
      cost: cfg.baseCost + cfg.costPerItem * items,
      category: meta.category,
    };
  });

  const totalCost = entries.reduce((sum, e) => sum + e.cost, 0);
  const maxCost = Math.max(...entries.map((e) => e.cost), 0.001);

  const categoryTotals = entries.reduce<Record<PhaseCategory, number>>(
    (acc, e) => ({ ...acc, [e.category]: (acc[e.category] ?? 0) + e.cost }),
    {} as Record<PhaseCategory, number>,
  );

  return (
    <div className="space-y-6 overflow-y-auto pr-1">
      {/* Alert */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <Info size={14} className="text-amber-500 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-700 leading-relaxed">
          <span className="font-bold">Estimated costs</span> based on typical Gemini API pricing.
          Actual token counts are not yet tracked in the pipeline — backend instrumentation is roadmapped.
          These figures reflect the model and approximate tokens used per phase.
        </p>
      </div>

      {entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-center">
          <DollarSign size={28} className="text-slate-200 mb-3" />
          <p className="text-sm text-slate-500">No cost data available yet.</p>
          <p className="text-xs text-slate-400 mt-1">Complete at least one pipeline step to see estimates.</p>
        </div>
      ) : (
        <>
          {/* Total summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Total Est. Cost</p>
              <p className="text-2xl font-bold text-navy-900">{formatCost(totalCost)}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">AI Phases</p>
              <p className="text-2xl font-bold text-navy-900">{entries.length}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Creative Cost</p>
              <p className="text-2xl font-bold text-teal-600">
                {formatCost(categoryTotals.creative ?? 0)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Cognitive Cost</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatCost(categoryTotals.cognitive ?? 0)}
              </p>
            </div>
          </div>

          {/* Bar chart */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-bold text-slate-500 uppercase mb-4">Cost by Phase</p>
            <div className="space-y-3">
              {entries
                .sort((a, b) => b.cost - a.cost)
                .map((entry) => {
                  const cat = CATEGORY_STYLE[entry.category];
                  const pct = (entry.cost / maxCost) * 100;
                  return (
                    <div key={entry.phase} className="flex items-center gap-3">
                      <span className="text-xs text-slate-600 w-36 shrink-0 truncate">
                        {phaseLabel(entry.phase)}
                      </span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div
                          className={clsx('h-full rounded-full transition-all', cat.bar)}
                          style={{ width: `${Math.max(pct, 2)}%` }}
                        />
                      </div>
                      <span className="text-[11px] font-mono text-slate-500 w-16 text-right shrink-0">
                        {formatCost(entry.cost)}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Breakdown table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Phase</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Model</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Category</th>
                  <th className="text-right px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Items</th>
                  <th className="text-right px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Est. Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((entry) => {
                  const cat = CATEGORY_STYLE[entry.category];
                  return (
                    <tr key={entry.phase} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-navy-900">{phaseLabel(entry.phase)}</td>
                      <td className="px-4 py-2.5 text-slate-500 font-mono">{entry.model}</td>
                      <td className="px-4 py-2.5">
                        <span className={clsx('text-[10px] font-bold px-2 py-0.5 rounded-full border', cat.badge)}>
                          {cat.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{entry.items}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold text-navy-900">
                        {formatCost(entry.cost)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-slate-50 font-bold border-t border-slate-200">
                  <td className="px-4 py-2.5 text-navy-900" colSpan={4}>Total</td>
                  <td className="px-4 py-2.5 text-right font-mono text-navy-900">{formatCost(totalCost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Output tab
// ---------------------------------------------------------------------------

function OutputTab({ job }: { job: AdPackStatusResponse }) {
  const manifest = job.manifest;
  const summary = manifest?.summary;
  const rankedAds = manifest?.ranked_ads ?? [];

  if (!manifest && job.status !== 'completed') {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-center">
        <Sparkles size={32} className="text-slate-200 mb-3" />
        <p className="text-sm text-slate-500">Output available once the job completes.</p>
        <p className="text-xs text-slate-400 mt-1">The final pack manifest, ranked ads, and quality metrics will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-y-auto pr-1">
      {/* Quality metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Ads Generated</p>
          <p className="text-2xl font-bold text-navy-900">{job.ads_generated}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Ads Reviewed</p>
          <p className="text-2xl font-bold text-navy-900">{job.ads_reviewed}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Personas</p>
          <p className="text-2xl font-bold text-navy-900">{job.personas_count}</p>
        </div>
        {summary?.mean_ces != null ? (
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4">
            <p className="text-[10px] text-emerald-700 font-bold uppercase mb-1">Mean CES Score</p>
            <p className="text-2xl font-bold text-emerald-700">
              {(summary.mean_ces * 100).toFixed(0)}%
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Total Phases</p>
            <p className="text-2xl font-bold text-navy-900">
              {job.phases_completed.length}
            </p>
          </div>
        )}
      </div>

      {/* CES gauge */}
      {summary?.mean_ces != null && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-500 uppercase">Cognitive Engagement Score (CES)</p>
            <span className="text-lg font-bold text-emerald-600">
              {(summary.mean_ces * 100).toFixed(1)}%
            </span>
          </div>
          <div className="bg-slate-100 rounded-full h-3 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-teal-400 to-emerald-500 transition-all duration-1000"
              style={{ width: `${(summary.mean_ces * 100).toFixed(0)}%` }}
            />
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Measures predicted cognitive engagement and purchase intent resonance across personas.
          </p>
        </div>
      )}

      {/* Ranked ads */}
      {rankedAds.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs font-bold text-slate-500 uppercase mb-4">Top Ranked Ads</p>
          <div className="space-y-3">
            {rankedAds.map((ad, i) => (
              <div
                key={ad.brief_id}
                className={clsx(
                  'flex items-center gap-4 p-3 rounded-xl border',
                  i === 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200',
                )}
              >
                <div
                  className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0',
                    i === 0 ? 'bg-amber-400 text-white' : 'bg-slate-300 text-slate-600',
                  )}
                >
                  {i === 0 ? <Star size={14} /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy-900 truncate">{ad.headline}</p>
                  <p className="text-xs text-slate-400">{ad.persona}</p>
                </div>
                <div className="shrink-0 text-right">
                  <span
                    className={clsx(
                      'text-sm font-bold',
                      i === 0 ? 'text-amber-600' : 'text-emerald-600',
                    )}
                  >
                    {(ad.ces * 100).toFixed(0)}%
                  </span>
                  <p className="text-[10px] text-slate-400">CES</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pipeline summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <p className="text-xs font-bold text-slate-500 uppercase mb-4">Pipeline Completion</p>
        <div className="space-y-2">
          {(['init', 'data', 'cognitive', 'creative', 'output'] as PhaseCategory[]).map((cat) => {
            const style = CATEGORY_STYLE[cat];
            const catSteps = (job.execution_log?.steps ?? []).filter(
              (s) => getPhaseMeta(s.step_name).category === cat,
            );
            const done = catSteps.filter((s) => s.status === 'completed').length;
            const total = catSteps.length;
            if (total === 0) return null;
            return (
              <div key={cat} className="flex items-center gap-3">
                <span className={clsx('text-[10px] font-bold px-2 py-0.5 rounded-full border w-20 text-center', style.badge)}>
                  {style.label}
                </span>
                <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full transition-all', style.bar)}
                    style={{ width: total > 0 ? `${(done / total) * 100}%` : '0%' }}
                  />
                </div>
                <span className="text-[10px] text-slate-500 w-12 text-right font-mono">
                  {done}/{total}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

interface AgenticDeepDiveModalProps {
  job: AdPackStatusResponse;
  onClose: () => void;
}

export function AgenticDeepDiveModal({ job, onClose }: AgenticDeepDiveModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('intelligence');
  const [highlightedPhase, setHighlightedPhase] = useState<string | null>(null);

  const isLive = ['running', 'pending', 'queued'].includes(job.status);
  const steps = job.execution_log?.steps ?? [];
  const insights = job.execution_log?.insight_log ?? [];
  const totalCost = estimateTotalCost(steps.filter((s) => s.status === 'completed' || s.status === 'running'));

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handlePhaseClick = (phase: string) => {
    setHighlightedPhase((p) => (p === phase ? null : phase));
  };

  const tabs: Array<{ id: TabId; icon: React.ElementType; label: string; count?: number }> = [
    { id: 'intelligence', icon: Brain, label: 'Intelligence', count: insights.length },
    { id: 'architecture', icon: GitFork, label: 'Architecture' },
    { id: 'pipeline', icon: Zap, label: 'Pipeline', count: steps.length },
    { id: 'cost', icon: DollarSign, label: 'Cost Analysis' },
    { id: 'output', icon: TrendingUp, label: 'Output' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-900/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative flex-1 flex flex-col m-4 md:m-8 bg-slate-50 rounded-2xl shadow-2xl overflow-hidden max-h-[calc(100vh-4rem)]">
        {/* Header */}
        <div className="bg-navy-900 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-brand-teal/20 flex items-center justify-center">
              <Sparkles size={18} className="text-brand-teal" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-base font-bold text-white">Agentic Intelligence Deep Dive</h2>
                {isLive ? (
                  <span className="flex items-center gap-1.5 text-[10px] font-medium text-brand-teal">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-teal" />
                    </span>
                    LIVE
                  </span>
                ) : (
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    Completed
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                {job.client_uid} · {job.job_id}
              </p>
            </div>
          </div>

          {/* Key stats */}
          <div className="hidden md:flex items-center gap-6 mr-8">
            {[
              { label: 'Phases', value: job.phases_completed.length + (isLive ? `/${job.phases_completed.length + job.phases_remaining.length + 1}` : '') },
              { label: 'Insights', value: insights.length },
              { label: 'Runtime', value: elapsed(job.elapsed_seconds) },
              { label: 'Est. Cost', value: formatCost(totalCost) },
            ].map(({ label, value }) => (
              <div key={label} className="text-center">
                <p className="text-[10px] text-slate-500 uppercase font-bold">{label}</p>
                <p className="text-sm font-bold text-white">{value}</p>
              </div>
            ))}
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tab bar */}
        <div className="bg-white border-b border-slate-200 px-6 flex items-center gap-1 shrink-0">
          {tabs.map(({ id, icon: Icon, label, count }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-3 text-xs font-semibold border-b-2 transition-colors',
                activeTab === id
                  ? 'text-navy-900 border-brand-teal'
                  : 'text-slate-500 border-transparent hover:text-slate-700',
              )}
            >
              <Icon size={13} />
              {label}
              {count != null && count > 0 && (
                <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 rounded-full font-mono">
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden p-6">
          {activeTab === 'intelligence' && (
            <IntelligenceTab
              job={job}
              highlightedPhase={highlightedPhase}
              onPhaseClick={handlePhaseClick}
            />
          )}
          {activeTab === 'architecture' && <ArchitectureTab job={job} />}
          {activeTab === 'pipeline' && <PipelineTab job={job} />}
          {activeTab === 'cost' && <CostAnalysisTab job={job} />}
          {activeTab === 'output' && <OutputTab job={job} />}
        </div>
      </div>
    </div>
  );
}
